import {createSelector} from 'reselect';

import firmwareStatusTypes from '../../consts/device/firmwareStatusTypes';
import codentifyTransformService from '../../services/codentifyTransformService';
import deviceTypesService from '../../services/device/deviceTypesService';
import fwuTargetService from '../../services/device/fwuTargetService';
import iotDeviceConnectionStatusService from '../../services/iotDevice/iotDeviceConnectionStatusService';
import objUtils from '../../utils/objUtils';

const selectState = (state) => state.iotDevice;

const makeSelectIotDevice = () => createSelector(selectState, (state) => state);

const selectIsIotDeviceDataExists = createSelector(makeSelectIotDevice(), (iotDevice) => !!iotDevice.data);

const makeSelectDeviceConnectionStatus = () =>
    createSelector(makeSelectIotDevice(), (iotDevice) => iotDevice.deviceConnectionStatus);

const selectIsDeviceDisconnected = createSelector(makeSelectDeviceConnectionStatus(), (status) =>
    iotDeviceConnectionStatusService.isDeviceDisconnected(status)
);

const selectIsDevicePaired = createSelector(makeSelectDeviceConnectionStatus(), (status) =>
    iotDeviceConnectionStatusService.isDevicePaired(status)
);

const makeSelectIsDeviceConnected = () =>
    createSelector(makeSelectDeviceConnectionStatus(), (status) =>
        iotDeviceConnectionStatusService.isDeviceConnected(status)
    );

const makeSelectIsPairingInProgress = () =>
    createSelector(makeSelectIotDevice(), (iotDevice) => iotDevice.isPairingInProgress);

const makeSelectIsDeviceReady = () =>
    createSelector(makeSelectDeviceConnectionStatus(), (status) =>
        iotDeviceConnectionStatusService.isDeviceReady(status)
    );

const makeSelectIsWrongDevice = () =>
    createSelector(makeSelectDeviceConnectionStatus(), (status) =>
        iotDeviceConnectionStatusService.isWrongDevice(status)
    );

const makeSelectIsDeviceNotSupported = () =>
    createSelector(makeSelectDeviceConnectionStatus(), (status) =>
        iotDeviceConnectionStatusService.isDeviceNotSupported(status)
    );

const makeSelectIotDeviceData = () =>
    createSelector(makeSelectIotDevice(), (iotDevice) => {
        if (!iotDevice?.data) return objUtils.returnEmptyObjectIfUndefined(iotDevice?.data);

        const {device} = iotDevice?.data;
        const {type, codentify, deviceSerialNumber} = device || {};
        const isP4 = deviceTypesService.isP4(type);
        const isP1V4 = deviceTypesService.isP1V4(type);
        const preparedCodentify = codentifyTransformService.transform(isP4, codentify); //transform modify codentify only for P4 device and on DCS market
        const preparedDevice = {
            ...device,
            codentify: preparedCodentify,
            codentifyOriginal: codentify,
            isP4,
            isP1V4,
        };
        const iotData = iotDevice?.data;

        if (iotData && !iotData.deviceSerialNumber) {
            iotData.deviceSerialNumber = deviceSerialNumber;
        }

        const iotDeviceData = {...iotDevice?.data, device: preparedDevice};

        return iotDeviceData;
    });

const selectFirmwareState = createSelector(makeSelectIotDevice(), (iotDevice) =>
    objUtils.returnEmptyObjectIfUndefined(iotDevice?.firmware)
);

const selectFirmwareProgress = createSelector(selectFirmwareState, (state) => state.progress);

const makeSelectFirmwareProgressByFwuTarget = (fwuTarget) =>
    createSelector(selectFirmwareState, (state) => (state.fwuTarget === fwuTarget ? state.progress : 0));

const selectIsFirmwareError = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isError(state.firmwareStatus)
);

const selectFirmwareError = createSelector(selectFirmwareState, (state) => state.error);

const selectIsFirmwareStarted = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isStarted(state.firmwareStatus)
);

const selectIsFirmwareFinished = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isFinished(state.firmwareStatus)
);

const selectIsFirmwareSuccess = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isSuccess(state.firmwareStatus)
);

const selectIsFirmwareChecking = createSelector(selectFirmwareState, (state) =>
    firmwareStatusTypes.isChecking(state.firmwareStatus)
);

const makeSelectIsFirmwareProcessStarted = (fwuTarget) =>
    createSelector(makeSelectFirmwareProgressByFwuTarget(fwuTarget), (progress) => progress > 0);

const selectIsFirmwareInProgress = createSelector(
    selectIsFirmwareChecking,
    selectIsFirmwareStarted,
    selectIsFirmwareFinished,
    (isChecking, isStarted, isFinished) => isChecking || isStarted || isFinished
);

const FWU_COMPLETED_PROGRESS = 100;

const selectIsFirmwareCompleted = createSelector(
    selectIsFirmwareInProgress,
    selectFirmwareProgress,
    (isFirmwareInProgress, progress) => isFirmwareInProgress && progress === FWU_COMPLETED_PROGRESS
);

const selectFirmwareFwuTarget = createSelector(selectFirmwareState, (state) => state.fwuTarget);

const selectIsFirmwareTargetDevice = createSelector(selectFirmwareFwuTarget, (fwuTarget) =>
    fwuTargetService.isFwuTargetDevice(fwuTarget)
);

const selectIsFirmwareTargetBleStack = createSelector(selectFirmwareFwuTarget, (fwuTarget) =>
    fwuTargetService.isFwuTargetBleStack(fwuTarget)
);

const selectIsIotDeviceP4 = createSelector(makeSelectIotDevice(), (iotDevice) =>
    deviceTypesService.isP4(iotDevice?.data?.device?.type)
);

const selectIsIotDeviceDelta = createSelector(makeSelectIotDevice(), (iotDevice) =>
    deviceTypesService.isDelta(iotDevice?.data?.device?.type)
);

const selectIotDeviceType = createSelector(makeSelectIotDevice(), (iotDevice) => iotDevice?.data?.device?.type);

const selectIotHolderType = createSelector(makeSelectIotDevice(), (iotDevice) => iotDevice?.data?.holder?.type);

const selectIsDeviceInSblMode = createSelector(
    makeSelectIotDevice(),
    (iotDevice) => iotDevice?.data?.device?.is_device_in_sbl_mode
);

const selectIsHolderConnected = createSelector(
    makeSelectIotDeviceData(),
    (iotDevice) => iotDevice?.device?.isHolderConnected
);

const selectFeaturesConfigUpdateDate = createSelector(
    makeSelectIotDevice(),
    (iotDevice) => iotDevice.featuresConfigUpdateDate
);

export {
    makeSelectDeviceConnectionStatus,
    makeSelectFirmwareProgressByFwuTarget,
    makeSelectIotDeviceData,
    makeSelectIsDeviceConnected,
    makeSelectIsDeviceNotSupported,
    makeSelectIsDeviceReady,
    makeSelectIsFirmwareProcessStarted,
    makeSelectIsPairingInProgress,
    makeSelectIsWrongDevice,
    selectFeaturesConfigUpdateDate,
    selectFirmwareError,
    selectFirmwareFwuTarget,
    selectFirmwareProgress,
    selectIotDeviceType,
    selectIotHolderType,
    selectIsDeviceDisconnected,
    selectIsDeviceInSblMode,
    selectIsDevicePaired,
    selectIsFirmwareChecking,
    selectIsFirmwareCompleted,
    selectIsFirmwareError,
    selectIsFirmwareFinished,
    selectIsFirmwareInProgress,
    selectIsFirmwareStarted,
    selectIsFirmwareSuccess,
    selectIsFirmwareTargetBleStack,
    selectIsFirmwareTargetDevice,
    selectIsHolderConnected,
    selectIsIotDeviceDataExists,
    selectIsIotDeviceDelta,
    selectIsIotDeviceP4,
};
